import {
  QueryClient,
  keepPreviousData,
  type DefaultOptions,
} from "@tanstack/react-query";

import type { ApiError } from "./api";

function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    typeof (error as { status: unknown }).status === "number"
  );
}

// Retry policy. Never retry a 4xx client error: a 429 rate-limit rejection
// only deepens if retried (the request still counts against the window), and
// 401/403/404/422 won't change on a second identical request. Transient
// failures — a network error (status 0, tagged in lib/api `request()`) or a
// 5xx — get a single retry. Anything without a recognisable status also gets
// one retry so an unexpected shape doesn't silently wedge the query.
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < 1;
}

// Exponential backoff so the single transient retry doesn't fire instantly
// (and so a flaky server isn't hammered). Capped at 30s.
export function retryDelayMs(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 30_000);
}

// The app-wide React Query defaults. Exported (not inlined into
// makeQueryClient) so component tests can build their QueryClient from the
// SAME cache behaviour the browser gets — a test client that omits these can
// pass while the real app fails. The load-bearing example: the global
// `placeholderData: keepPreviousData` below. When a query's key flips to a
// disabled query (e.g. a background-job status poll whose job id is reset to
// null), keepPreviousData serves the previous key's data as a placeholder
// indefinitely — the disabled query never fetches to replace it. That is why
// "Clear scenarios" on GenerateScenariosView needed a per-query opt-out
// (Wave 11, Task #547/#548); tests must mirror this default to catch it.
export const queryClientDefaultOptions: DefaultOptions = {
  queries: {
    // Polling is OPT-IN, not a global default. Queries that genuinely
    // need live updates set their own refetchInterval (e.g. the job /
    // run status pollers in DialpadView and Phase3AssessmentsCard, 2–3s).
    // Everything else is reference / list / config / aggregate data that
    // refreshes via mutation invalidation, so it must not poll. A global
    // refetchInterval here makes EVERY mounted query a poller and the
    // aggregate request rate overruns the API rate limiter (HTTP 429).
    staleTime: 30_000,
    // No synchronized burst when the tab regains focus — the live pollers
    // above already keep their own data current.
    refetchOnWindowFocus: false,
    retry: shouldRetry,
    retryDelay: retryDelayMs,
    placeholderData: keepPreviousData,
  },
};

export function makeQueryClient(): QueryClient {
  return new QueryClient({ defaultOptions: queryClientDefaultOptions });
}
